import { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';
import logo from '../../assets/logo.png';

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const MyStoreLogin = ({ onAuthSuccess }) => {
  const { theme } = useTheme();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (mode === 'signup') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });
        if (signUpError) throw signUpError;

        // Upsert user row with location flags
        const { error: upsertError } = await supabase
          .from('users')
          .upsert({
            auth_id: data.user.id,
            email: email,
            is_location: true,
            intake_completed: false,
            approved_by_admin: false,
            contract_agreed: false,
          }, { onConflict: 'auth_id' });

        if (upsertError) throw upsertError;
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) throw signInError;
      }

      if (onAuthSuccess) onAuthSuccess();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    card: {
      width: '100%',
      maxWidth: 400,
    },
    logoSection: {
      textAlign: 'center',
      marginBottom: 32,
    },
    logo: {
      width: 100,
      height: 100,
      borderRadius: 20,
      objectFit: 'cover',
      marginBottom: 12,
    },
    title: {
      fontSize: 24,
      fontWeight: 700,
      color: theme.text,
      margin: '0 0 4px',
    },
    subtitle: {
      fontSize: 14,
      color: theme.textSecondary,
      margin: 0,
    },
    portalBadge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: 20,
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      color: '#0ea5e9',
      fontSize: 12,
      fontWeight: 600,
      marginTop: 8,
    },
    error: {
      padding: 12,
      borderRadius: 10,
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
      fontSize: 13,
      marginBottom: 16,
      textAlign: 'center',
    },
    fieldGroup: {
      marginBottom: 16,
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: theme.text,
      marginBottom: 6,
    },
    input: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
      color: theme.text,
      fontSize: 15,
      boxSizing: 'border-box',
      outline: 'none',
    },
    passwordWrapper: {
      position: 'relative',
    },
    passwordToggle: {
      position: 'absolute',
      right: 12,
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: theme.textMuted,
      cursor: 'pointer',
      padding: 0,
      display: 'flex',
    },
    button: {
      width: '100%',
      padding: 14,
      borderRadius: 12,
      border: 'none',
      backgroundColor: theme.accent,
      color: '#fff',
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: 8,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    toggle: {
      textAlign: 'center',
      marginTop: 20,
      fontSize: 14,
      color: theme.textSecondary,
    },
    toggleLink: {
      color: theme.accent,
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      fontSize: 14,
      fontWeight: 600,
      padding: 0,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoSection}>
          <img src={logo} alt="CardChase" style={styles.logo} />
          <h1 style={styles.title}>CardChase</h1>
          <p style={styles.subtitle}>Location Partner Portal</p>
          <div style={styles.portalBadge}>Store Management</div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              placeholder="you@yourbusiness.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                disabled={loading}
              />
              <button
                type="button"
                style={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
              >
                <EyeIcon open={!showPassword} />
              </button>
            </div>
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            disabled={loading}
          >
            {loading ? 'Please wait...' : (mode === 'signin' ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div style={styles.toggle}>
          {mode === 'signin' ? (
            <>
              Need an account?{' '}
              <button style={styles.toggleLink} onClick={() => { setMode('signup'); setError(''); }}>
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button style={styles.toggleLink} onClick={() => { setMode('signin'); setError(''); }}>
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyStoreLogin;
